import { Module, ModuleOptionsValue } from '@/modules/companies'
import { AbsenceReportPerUserCard } from '@/modules/companies/CompanySettings/components/AbsenceSettings/absence-report-user-card'
import { ExportActivitiesForSalaryCard } from '@/modules/companies/CompanySettings/components/ExportsSettings/ExportActivitiesForSalaryCard'
import { ExportCompanyIssuesCard } from '@/modules/companies/CompanySettings/components/ExportsSettings/ExportCompanyIssuesCard'
import { ExportDriverActivitiesCard } from '@/modules/companies/CompanySettings/components/ExportsSettings/ExportDriverActivitiesCard'
import { ExportDriverGradesCard } from '@/modules/companies/CompanySettings/components/ExportsSettings/ExportDriverGradesCard'
import { ExportFleetTaxZoneCard } from '@/modules/companies/CompanySettings/components/ExportsSettings/ExportFleetTaxZoneCard'
import { ExportInfringementsCard } from '@/modules/companies/CompanySettings/components/ExportsSettings/ExportInfringementsCard'
import { ExportTimesheetsCard } from '@/modules/companies/CompanySettings/components/ExportsSettings/ExportTimesheetsCard'
import { ExportUnknownDriverLocations } from '@/modules/companies/CompanySettings/components/ExportsSettings/ExportUnknownDriverLocations'
import { ExportActivityExceptionReportCard } from '@/modules/companies/CompanySettings/components/ExportsSettings/export-activity-exception-report-card'
import { ExportArbeidstilsynetCard } from '@/modules/companies/CompanySettings/components/ExportsSettings/export-arbeidstilsynet-card'
import { ExportSalaryCorrectionsCard } from '@/modules/companies/CompanySettings/components/ExportsSettings/export-salary-corrections-card'
import { useModuleConfigs } from '@/modules/companies/CompanySettings/hooks'
import { EXPORT_START_STOP_COUNTRY_REGISTRATIONS } from '@/modules/exports/mutations'
import { useAdmin, useModuleConfig, usePermissions } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { LucideIcon } from 'lucide-react'
import { MODULE_ICONS } from '@/modules/common/module-icons'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { ExportRawActivitiesCard } from '../../components/export-raw-activities-card'

export type ExportCategoryId =
  | 'activities'
  | 'arbeidstilsynet'
  | 'salary'
  | 'timesheets'
  | 'absences'
  | 'issues'
  | 'infringements'
  | 'driver-grades'
  | 'employer-tax'

export interface ExportCategory {
  id: ExportCategoryId
  label: string
  description: string
  icon: LucideIcon
  content: ReactNode
}

const LegacySalaryExportCard: React.FC = () => {
  const [loading, setLoading] = useState(false)

  return (
    <ExportActivitiesForSalaryCard
      loading={loading}
      onLoadingChange={setLoading}
    />
  )
}

export function useExportCategories(): ExportCategory[] {
  const translations = useTranslate({
    activities: 'common.activities',
    activitiesDescription: 'exports.category.activities-description',
    arbeidstilsynetDescription: 'exports.category.arbeidstilsynet-description',
    salary: 'common.salary',
    salaryDescription: 'exports.category.salary-description',
    timesheets: 'common.timesheets',
    timesheetsDescription: 'exports.category.timesheets-description',
    absences: 'settings.absence.reports.menu-title',
    absencesDescription: 'exports.category.absences-description',
    issues: 'common.issues',
    issuesDescription: 'exports.category.issues-description',
    infringements: 'common.infringements',
    infringementsDescription: 'exports.category.infringements-description',
    driverGrades: 'driver-grades.driver-grades',
    driverGradesDescription: 'exports.category.driver-grades-description',
    employerTax: 'common.employer-tax',
    employerTaxDescription: 'exports.category.employer-tax-description',
    startStopCountryReport: 'activities.start-stop-country-report',
  })

  const admin = useAdmin()
  const { active } = useModuleConfigs()
  const { hasPermissions } = usePermissions()
  const { getModuleOption } = useModuleConfig(Module.ACTIVITIES)

  const canExport = hasPermissions(PERMISSIONS.companies.export.exportReports)
  const canViewOthersAbsence = hasPermissions(
    PERMISSIONS.timesheets.view.othersabsence
  )
  const processesTachoFiles = getModuleOption(
    ModuleOptionsValue.ACTIVITIES_PROCESS_TACHO_FILES
  )

  const categories: (ExportCategory & { visible: boolean })[] = [
    {
      id: 'activities',
      label: translations.activities,
      description: translations.activitiesDescription,
      icon: MODULE_ICONS.drivingData,
      visible: canExport && active.activities,
      content: (
        <>
          <ExportDriverActivitiesCard />
          <ExportRawActivitiesCard />
          {processesTachoFiles && <ExportUnknownDriverLocations />}
          {processesTachoFiles && (
            <ExportActivityExceptionReportCard
              header={translations.startStopCountryReport}
              mutation={EXPORT_START_STOP_COUNTRY_REGISTRATIONS}
              resultKey="exportStartStopCountryRegistrations"
            />
          )}
        </>
      ),
    },
    {
      id: 'arbeidstilsynet',
      label: 'Arbeidstilsynet',
      description: translations.arbeidstilsynetDescription,
      icon: MODULE_ICONS.arbeidstilsynet,
      visible: canExport && active.activities,
      content: <ExportArbeidstilsynetCard />,
    },
    {
      id: 'salary',
      label: translations.salary,
      description: translations.salaryDescription,
      icon: MODULE_ICONS.workTimeAndSalary,
      visible: canExport && (active.salary || active.activities),
      content: active.salary ? (
        <ExportSalaryCorrectionsCard />
      ) : (
        <LegacySalaryExportCard />
      ),
    },
    {
      id: 'timesheets',
      label: translations.timesheets,
      description: translations.timesheetsDescription,
      icon: MODULE_ICONS.timeEntries,
      visible: canExport && active.timesheets,
      content: <ExportTimesheetsCard />,
    },
    {
      id: 'absences',
      label: translations.absences,
      description: translations.absencesDescription,
      icon: MODULE_ICONS.absences,
      visible: active.absences && canViewOthersAbsence,
      content: <AbsenceReportPerUserCard />,
    },
    {
      id: 'issues',
      label: translations.issues,
      description: translations.issuesDescription,
      icon: MODULE_ICONS.issues,
      visible: canExport && active.issues,
      content: <ExportCompanyIssuesCard />,
    },
    {
      id: 'infringements',
      label: translations.infringements,
      description: translations.infringementsDescription,
      icon: MODULE_ICONS.infringements,
      visible: canExport && active.infringements,
      content: <ExportInfringementsCard />,
    },
    {
      id: 'driver-grades',
      label: translations.driverGrades,
      description: translations.driverGradesDescription,
      icon: MODULE_ICONS.driverGrade,
      visible: canExport && active.driverGrade,
      content: <ExportDriverGradesCard />,
    },
    {
      id: 'employer-tax',
      label: translations.employerTax,
      description: translations.employerTaxDescription,
      icon: MODULE_ICONS.fleetManagement,
      visible: active.employerTax && admin,
      content: <ExportFleetTaxZoneCard />,
    },
  ]

  return categories.filter(category => category.visible)
}
